import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useAppActivation from 'CustomHooks/useAppActivation';
import { VisitWish, visitWishApi, VisitWishSettings } from '../../Screens/VisitWishes/visitWishApi';
import { filterOf } from '../../Screens/VisitWishes/wishFilters';

type InboxState = {
  scope: object;
  wishes: VisitWish[];
  settings: VisitWishSettings | null;
  loading: boolean;
  failed: boolean;
  hasMore: boolean;
};

const open = (wishes: VisitWish[]) => wishes.filter((wish) => filterOf(wish) !== 'done');
const REFRESH_DELAY_MS = 30000;

/** The first `pages` pages of open wishes (oldest first), and whether more follow. */
async function readOpenPages(restaurantId: string, pages: number) {
  const wishes: VisitWish[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < pages; page++) {
    const result = await visitWishApi.openPage(restaurantId, cursor);
    wishes.push(...result.wishes);
    cursor = result.openCursor ?? undefined;
    if (!cursor) break;
  }
  return { wishes, hasMore: !!cursor };
}

/**
 * The requests sidebar reads open wishes page by page, without creating
 * waiting-list entries: the first page, more only when asked (`loadMore`);
 * refreshes read the pages already shown. `hasMore` says that later wishes
 * exist which are not loaded yet. With `withSettings` it also reads the rules
 * the wish actions depend on; a failed settings read leaves the wishes usable
 * and the actions at their safe default.
 */
export default function useVisitWishInbox(restaurantId: string, { withSettings = false } = {}) {
  const activation = useAppActivation('visit_wishes', restaurantId);
  const enabled = activation === 'active';
  const [result, setResult] = useState<InboxState | null>(null);
  const scope = useMemo(() => ({}), [restaurantId, activation, withSettings]);
  const current = useRef(scope);
  current.current = scope;
  const pages = useRef({ scope, count: 1 });
  if (pages.current.scope !== scope) pages.current = { scope, count: 1 };
  const sequence = useRef(0);
  const mounted = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reading = useRef<{ scope: object; promise: Promise<void> } | null>(null);

  const read = useCallback((): Promise<void> => {
    if (!enabled || !mounted.current || current.current !== scope || document.hidden || !navigator.onLine) return Promise.resolve();
    if (reading.current?.scope === scope) return reading.current.promise;
    const pending = { scope, promise: Promise.resolve() };
    reading.current = pending;
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    const request = ++sequence.current;
    const stillCurrent = () => mounted.current && current.current === scope && sequence.current === request;
    setResult((previous) => ({
      scope,
      wishes: previous?.scope === scope ? previous.wishes : [],
      settings: previous?.scope === scope ? previous.settings : null,
      loading: previous?.scope === scope ? previous.loading : true,
      failed: previous?.scope === scope ? previous.failed : false,
      hasMore: previous?.scope === scope ? previous.hasMore : false,
    }));
    const settingsRead: Promise<VisitWishSettings | null> = withSettings
      ? visitWishApi.settings(restaurantId).catch(() => null)
      : Promise.resolve(null);
    pending.promise = Promise.all([readOpenPages(restaurantId, pages.current.count), settingsRead])
      .then(([{ wishes, hasMore }, settings]) => {
        if (stillCurrent()) setResult({ scope, wishes: open(wishes), settings, loading: false, failed: false, hasMore });
      })
      .catch(() => {
        if (stillCurrent()) {
          setResult((previous) => ({
            scope,
            wishes: previous?.scope === scope ? previous.wishes : [],
            settings: previous?.scope === scope ? previous.settings : null,
            loading: false,
            failed: true,
            hasMore: previous?.scope === scope ? previous.hasMore : false,
          }));
        }
      })
      .finally(async () => {
        await settingsRead;
        if (reading.current === pending) reading.current = null;
        if (mounted.current && current.current === scope && !document.hidden && navigator.onLine) {
          timer.current = setTimeout(read, REFRESH_DELAY_MS);
        }
      });
    return pending.promise;
  }, [restaurantId, enabled, scope, withSettings]);

  const reload = useCallback(async (): Promise<void> => {
    if (!enabled || !mounted.current || current.current !== scope) return;
    const previous = reading.current;
    if (previous?.scope === scope) {
      ++sequence.current;
      await previous.promise;
    }
    await read();
  }, [enabled, scope, read]);

  /** Shows the next page of open wishes too (read together with the pages already shown). */
  const loadMore = useCallback(async (): Promise<void> => {
    if (!enabled || !mounted.current || current.current !== scope || pages.current.scope !== scope) return;
    pages.current.count++;
    await reload();
  }, [enabled, scope, reload]);

  /** Takes over a wish the API returned after an action; a completed wish leaves the inbox. */
  const replace = useCallback(
    (updated: VisitWish) => {
      if (!enabled || !mounted.current || current.current !== scope) return;
      ++sequence.current;
      setResult((previous) =>
        previous?.scope === scope
          ? { ...previous, wishes: open(previous.wishes.map((wish) => (wish.id === updated.id ? { ...wish, ...updated } : wish))) }
          : previous
      );
    },
    [enabled, scope]
  );

  useEffect(() => {
    mounted.current = true;
    read();
    const onVisibilityChange = () => {
      if (document.hidden) {
        if (timer.current !== null) clearTimeout(timer.current);
        timer.current = null;
      } else {
        read();
      }
    };
    if (enabled) {
      window.addEventListener('focus', read);
      window.addEventListener('online', read);
      document.addEventListener('visibilitychange', onVisibilityChange);
    }
    return () => {
      mounted.current = false;
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = null;
      window.removeEventListener('focus', read);
      window.removeEventListener('online', read);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [read]);

  const visible = enabled && result?.scope === scope ? result : null;
  return {
    enabled,
    wishes: visible?.wishes ?? [],
    settings: visible?.settings ?? null,
    loading: enabled && (visible?.loading ?? true),
    failed: visible?.failed ?? false,
    hasMore: visible?.hasMore ?? false,
    reload,
    loadMore,
    replace,
  };
}
